import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import {
  AssistantSearchDTO,
  AssistantType,
  PhotoUploadResponse,
  UserPreferencesExtended,
} from "../apis/dto/UserPreferences";
import idm from "../apis/idm";
import { ErrorToastr } from "../components/utility/ErrorToastr";

export const fetchUserPreferences = createAsyncThunk<
  UserPreferencesExtended | null,
  string
>("UserPreferences", async (ucmnetid: string, thunkApi) => {
  try {
    const response = await idm.get(`/directory/userDetails/${ucmnetid}`, {
      withCredentials: true,
    });

    if (typeof response.data == "object") {
      return response.data as UserPreferencesExtended | null;
    } else {
      throw new Error("Error - " + response.data);
    }
  } catch (err: any) {
    let error: AxiosError = err; // cast the error for access
    if (!error.response) {
      throw err;
    }

    const meta = { errorMessage: err.message, status: error.response.status };
    const response = { responseMessage: error.response.data, meta: meta };
    return thunkApi.rejectWithValue(response);
  }
});

export const fetchAssistanTypes = createAsyncThunk<AssistantType[], void>(
  "AssistanTypes",
  async (_, thunkApi) => {
    try {
      const response = await idm.get(`/assistant-types`, {
        withCredentials: true,
      });
      return response.data as AssistantType[];
    } catch (err: any) {
      let error: AxiosError = err; // cast the error for access
      if (!error.response) {
        throw err;
      }

      const meta = { errorMessage: err.message, status: error.response.status };
      const response = { responseMessage: error.response.data, meta: meta };
      return thunkApi.rejectWithValue(response);
    }
  }
);

export const updateUserPreferences = createAsyncThunk<
  UserPreferencesExtended,
  UserPreferencesExtended
>("updateUserPreferences", async (data: UserPreferencesExtended, thunkApi) => {
  try {
    const response = await idm.post("/directory/userPreferences", data, {
      withCredentials: true,
    });
    if (response.status !== 200) {
      throw new Error("Error - " + response.data);
    }

    return data as UserPreferencesExtended;
  } catch (err: any) {
    let error: AxiosError = err; // cast the error for access
    if (!error.response) {
      throw err;
    }
    const meta = { errorMessage: err.message, status: error.response.status };
    const response = { responseMessage: error.response.data, meta: meta };
    return thunkApi.rejectWithValue(response);
  }
});

export interface AssistantSearchArgs {
  bossId: string;
  searchTerm: string;
}
export const fetchAssistantSearch = createAsyncThunk<
  AssistantSearchDTO[],
  AssistantSearchArgs
>("AssistantSearch", async (params: AssistantSearchArgs, thunkApi) => {
  try {
    const response = await idm.get(
      `/assistants/assistantSearch/${params.bossId}/${params.searchTerm}`,
      {
        withCredentials: true,
      }
    );
    return response.data as AssistantSearchDTO[];
  } catch (err: any) {
    let error: AxiosError = err; // cast the error for access
    if (!error.response) {
      throw err;
    }

    const meta = { errorMessage: err.message, status: error.response.status };
    const response = { responseMessage: error.response.data, meta: meta };
    return thunkApi.rejectWithValue(response);
  }
});

export const requestPhotoHeadshot = createAsyncThunk<AxiosResponse, string>(
  "requestPhotoHeadshot",
  async (ucmnetid: string, thunkApi) => {
    try {
      const response = await idm.get(`/directory/requestPhoto/${ucmnetid}`, {
        withCredentials: true,
      });

      if (typeof response.data == "object") {
        return response.data;
      } else {
        throw new Error("Error - " + response.data);
      }
    } catch (err: any) {
      let error: AxiosError = err; // cast the error for access
      if (!error.response) {
        throw err;
      }

      const meta = { errorMessage: err.message, status: error.response.status };
      const response = { responseMessage: error.response.data, meta: meta };
      return thunkApi.rejectWithValue(response);
    }
  }
);

export interface PhotoUploadRequest {
  file: File;
  ucmnetid: string;
}

export const uploadPhoto = createAsyncThunk<
  PhotoUploadResponse,
  PhotoUploadRequest
>(
  "uploadPhoto",

  async (photoUploadRequest: PhotoUploadRequest, thunkApi) => {
    try {
      const { file, ucmnetid } = photoUploadRequest;
      var fd = new FormData();
      fd.append("username", ucmnetid);
      fd.append("photo", file);
      const response = await idm.post(`/directory/userPhoto`, fd, {
        headers: { "Content-Type": undefined as any },
        withCredentials: true,
      });

      return response.data as PhotoUploadResponse;
    } catch (err: any) {
      let error: AxiosError = err; // cast the error for access
      if (!error.response) {
        throw err;
      }
      const meta = { errorMessage: err.message, status: error.response.status };
      const response = { responseMessage: error.response.data, meta: meta };
      return thunkApi.rejectWithValue(response);
    }
  }
);

interface PhotoResponse {
  response: any;
  userPreferencesExtended: UserPreferencesExtended;
}

export const fetchPhoto = createAsyncThunk<
  PhotoResponse,
  UserPreferencesExtended
>(
  "photoFetchUserPreferences",
  async (person: UserPreferencesExtended, thunkApi) => {
    try {
      let url;
      if (person.photoUrl) {
        const response = await idm.get(person.photoUrl, {
          responseType: "blob",
          withCredentials: person.localPhoto,
        });
        url = window.URL.createObjectURL(response.data);
      }
      const responseData: PhotoResponse = {
        response: url,
        userPreferencesExtended: person,
      };
      return responseData;
    } catch (err: any) {
      let error: AxiosError = err; // cast the error for access
      if (!error.response) {
        throw err;
      }

      const meta = { errorMessage: err.message, status: error.response.status };
      const response = { responseMessage: error.response.data, meta: meta };
      return thunkApi.rejectWithValue(response);
    }
  }
);

interface PersonState {
  error: ErrorToastr | null | undefined;
  loading: "idle" | "pending" | "succeeded" | "failed";
  entity: UserPreferencesExtended | undefined;
  AssistantTypes: {
    entities: AssistantType[];
    loading: "idle" | "pending" | "succeeded" | "failed";
    error: ErrorToastr | null | undefined;
  };
  AssistantSearch: {
    entities: AssistantSearchDTO[];
    loading: "idle" | "pending" | "succeeded" | "failed";
    error: ErrorToastr | null | undefined;
  };
}

// Then, handle actions in your reducers:
const personSlice = createSlice({
  name: "users",
  initialState: {
    entity: undefined,
    loading: "idle",
    error: null,
    AssistantTypes: {
      entities: [],
      loading: "idle",
      error: null,
    },
    AssistantSearch: {
      entities: [],
      loading: "idle",
      error: null,
    },
  } as PersonState,
  reducers: {
    resetUserState: (state, action: any) => {
      state.loading = "idle";
      state.entity = undefined;
      state.error = null;
    },
    resetPhotoUpload: (state) => {
      Object.assign(state.entity, {
        uploadPhoto: {
          loading: "idle",
          response: null as any,
          error: null,
        },
      });
    },
    resetSearchResults: (state, action: any) => {
      state.AssistantSearch.entities = [];
    },
    setLoading: (
      state,
      action: PayloadAction<"idle" | "pending" | "succeeded" | "failed">
    ) => {
      state.AssistantSearch.loading = action.payload;
    },
    // standard reducer logic, with auto-generated action types per reducer
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPreferences.pending, (state, action) => {
        if (state.loading === "idle") {
          state.loading = "pending";
        }
      })
      .addCase(
        fetchUserPreferences.fulfilled,
        (state, action: PayloadAction<UserPreferencesExtended | null>) => {
          if (action.payload) {
            state.entity = action.payload;
            Object.assign(state.entity, {
              photoLoading: {
                loading: "idle",
                error: null,
              },
              photo: null as any,
              localPhoto: false,
              uploadPhoto: {
                loading: "idle",
                response: null as any,
                error: null,
              },
              photoHeadshotAppointment: {
                loading: "idle",
                error: null,
              },
            });
            state.entity.photoId = action.payload?.photoId;
            state.entity.photoLoading.loading = "idle";
            state.entity.localPhoto =
              state.entity.photoUrl != null &&
              !state.entity.photoUrl
                .toLowerCase()
                .startsWith("https://storage.googleapis.com");
          }
          state.loading = "succeeded";
        }
      )
      .addCase(fetchUserPreferences.rejected, (state: any, action: any) => {
        if (action.payload) {
          const errorObj: ErrorToastr = {
            title: "!Error - " + action.payload.meta.status,
            message:
              action.payload && action.payload.responseMessage.message
                ? action.payload.responseMessage.message
                : action.payload.meta.errorMessage,
          };
          state.error = errorObj;
        } else {
          const errorObj: ErrorToastr = {
            title: "!Unknown Error",
            message: "Contact Help Desk - " + action.error.message,
          };
          state.error = errorObj;
        }

        state.loading = "failed";
      })
      .addCase(fetchPhoto.pending, (state, action) => {
        if (state.entity) {
          if (state.entity.photoLoading.loading === "idle") {
            state.entity.photoLoading.loading = "pending";
          }
        }
      })
      .addCase(
        fetchPhoto.fulfilled,
        (state, action: PayloadAction<PhotoResponse>) => {
          if (state.entity) {
            state.entity.photo = action.payload.response;
            state.entity.photoLoading.loading = "succeeded";
          }
        }
      )
      .addCase(fetchPhoto.rejected, (state: any, action: any) => {
        if (state.entity && state.entity.photoLoading) {
          state.entity.photoLoading.loading = "failed";

          if (action.payload) {
            const errorObj: ErrorToastr = {
              title: "!Error - " + action.payload.meta.status,
              message:
                action.payload && action.payload.responseMessage.message
                  ? action.payload.responseMessage.message
                  : action.payload.meta.errorMessage,
            };
            state.entity.photoLoading.error = errorObj;
          } else {
            const errorObj: ErrorToastr = {
              title: "!Unknown Error",
              message: "Contact Help Desk - " + action.error.message,
            };
            state.entity.photoLoading.error = errorObj;
          }
        }
      })
      .addCase(requestPhotoHeadshot.pending, (state, action) => {
        if (state.entity) {
          if (state.entity.photoHeadshotAppointment.loading === "idle") {
            state.entity.photoHeadshotAppointment.loading = "pending";
          }
        }
      })
      .addCase(requestPhotoHeadshot.fulfilled, (state, action) => {
        if (state.entity) {
          state.entity.photoHeadshotAppointment.loading = "succeeded";
        }
      })
      .addCase(requestPhotoHeadshot.rejected, (state: any, action: any) => {
        if (state.entity && state.entity.photoHeadshotAppointment) {
          state.entity.photoHeadshotAppointment.loading = "failed";

          if (action.payload) {
            const errorObj: ErrorToastr = {
              title: "!Error - " + action.payload.meta.status,
              message:
                action.payload && action.payload.responseMessage.message
                  ? action.payload.responseMessage.message
                  : action.payload.meta.errorMessage,
            };
            state.entity.photoHeadshotAppointment.error = errorObj;
          } else {
            const errorObj: ErrorToastr = {
              title: "!Unknown Error",
              message: "Contact Help Desk - " + action.error.message,
            };
            state.entity.photoHeadshotAppointment.error = errorObj;
          }
        }
      })
      .addCase(uploadPhoto.pending, (state, action) => {
        if (state.entity) {
          if (state.entity.uploadPhoto.loading !== "pending") {
            state.entity.uploadPhoto.loading = "pending";
          }
        }
      })
      .addCase(
        uploadPhoto.fulfilled,
        (state, action: PayloadAction<PhotoUploadResponse>) => {
          if (state.entity) {
            state.entity.uploadPhoto.loading = "succeeded";
            state.entity.uploadPhoto.response = action.payload;
          }
        }
      )
      .addCase(uploadPhoto.rejected, (state: any, action: any) => {
        if (state.entity && state.entity.uploadPhoto.loading === "pending") {
          if (action.payload) {
            const errorObj: ErrorToastr = {
              title: "Error - " + action.payload.meta.status,
              message: action.payload.responseMessage.problems.length
                ? action.payload.responseMessage.problems[0]
                : action.payload.meta.errorMessage,
            };
            state.entity.uploadPhoto.error = errorObj;
          } else {
            const errorObj: ErrorToastr = {
              title: "!Unknown Error",
              message: "Contact Help Desk - " + action.error.message,
            };
            state.entity.uploadPhoto.error = errorObj;
          }

          state.entity.uploadPhoto.loading = "failed";
        }
      })
      .addCase(fetchAssistanTypes.pending, (state, action) => {
        if (state.AssistantTypes.loading === "idle") {
          state.AssistantTypes.loading = "pending";
        }
      })
      .addCase(
        fetchAssistanTypes.fulfilled,
        (state, action: PayloadAction<AssistantType[]>) => {
          state.AssistantTypes.loading = "succeeded";
          state.AssistantTypes.entities = action.payload;
        }
      )
      .addCase(fetchAssistanTypes.rejected, (state: any, action: any) => {
        if (state.AssistantTypes && state.AssistantTypes.loading) {
          state.AssistantTypes.loading = "failed";

          if (action.payload) {
            const errorObj: ErrorToastr = {
              title: "!Error - " + action.payload.meta.status,
              message:
                action.payload && action.payload.responseMessage.message
                  ? action.payload.responseMessage.message
                  : action.payload.meta.errorMessage,
            };
            state.AssistantTypes.error = errorObj;
          } else {
            const errorObj: ErrorToastr = {
              title: "!Unknown Error",
              message: "Contact Help Desk - " + action.error.message,
            };
            state.AssistantTypes.error = errorObj;
          }
        }
      })
      .addCase(fetchAssistantSearch.pending, (state, action) => {
        if (state.AssistantSearch.loading === "idle") {
          state.AssistantSearch.loading = "pending";
        }
      })
      .addCase(
        fetchAssistantSearch.fulfilled,
        (state, action: PayloadAction<AssistantSearchDTO[]>) => {
          state.AssistantSearch.loading = "succeeded";
          state.AssistantSearch.entities = action.payload;
        }
      )
      .addCase(fetchAssistantSearch.rejected, (state: any, action: any) => {
        if (
          state.AssistantSearch &&
          state.AssistantSearch.loading === "pending"
        ) {
          if (action.payload) {
            const errorObj: ErrorToastr = {
              title: "!Error - " + action.payload.meta.status,
              message:
                action.payload && action.payload.responseMessage.message
                  ? action.payload.responseMessage.message
                  : action.payload.meta.errorMessage,
            };
            state.AssistantSearch.error = errorObj;
          } else {
            const errorObj: ErrorToastr = {
              title: "!Unknown Error",
              message: "Contact Help Desk - " + action.error.message,
            };
            state.AssistantSearch.error = errorObj;
          }

          state.AssistantSearch.loading = "failed";
        }
      })
      .addCase(updateUserPreferences.pending, (state, action) => {
        if (state.loading !== "pending") {
          state.loading = "pending";
        }
      })
      .addCase(
        updateUserPreferences.fulfilled,
        (state, action: PayloadAction<UserPreferencesExtended>) => {
          if (action && action.payload) {
            state.entity = action.payload;
          }
          state.loading = "succeeded";
        }
      )
      .addCase(updateUserPreferences.rejected, (state: any, action: any) => {
        if (state.loading === "pending") {
          if (action.payload) {
            state.error = {
              title: "!Error - " + action.payload.meta.status,
              message:
                action.payload && action.payload.responseMessage.message
                  ? action.payload.responseMessage.message
                  : action.payload.meta.errorMessage,
            };
          } else {
            state.error = {
              title: "!Unknown Error",
              message: "Contact Help Desk - " + action.error.message,
            };
          }

          state.loading = "failed";
        }
      });
  },
});

export const {
  resetSearchResults,
  setLoading,
  resetUserState,
  resetPhotoUpload,
} = personSlice.actions;
export default personSlice.reducer;
